import type {
    FormAnswerMap,
    PageLogic,
    PageLogicAction,
    PageLogicCondition,
    PageLogicPageRef,
    PageLogicResolution,
} from "./types";
import type { Json } from "@patient-watch/types";
import { isOwnOptionValue } from "./ownOptionValue";

function pageLogicActionToJson(action: PageLogicAction): Json {
    return action.action === "jump"
        ? { action: action.action, target_page_id: action.target_page_id }
        : { action: action.action };
}

export function pageLogicToJson(logic: PageLogic | null): Json | null {
    if (!logic) return null;

    return {
        rules: logic.rules.map((rule) => ({
            when: {
                input_id: rule.when.input_id,
                operator: rule.when.operator,
                value: rule.when.value,
            },
            // oxlint-disable-next-line -- `then` is the persisted PageLogic schema field.
            then: pageLogicActionToJson(rule.then),
        })),
        fallback: pageLogicActionToJson(logic.fallback),
    };
}

function normaliseAnswer(value: FormAnswerMap[string]): string | number | boolean | string[] | null {
    if (value == null) return null;
    if (typeof value === "object" && "branch" in value && "value" in value) {
        const branchValue = value as { branch?: string; value?: number };
        return branchValue.value ?? null;
    }
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === "boolean" || typeof value === "number") return value;
    return String(value);
}

function toFiniteNumber(value: unknown): number | null {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function compareNumbers(
    raw: ReturnType<typeof normaliseAnswer>,
    expected: PageLogicCondition["value"],
    compare: (left: number, right: number) => boolean
): boolean {
    const left = toFiniteNumber(raw);
    const right = toFiniteNumber(expected);
    if (left == null || right == null) return false;
    return compare(left, right);
}

/** Whether one page-logic condition holds for these answers. Also used for in-page show/hide (inputs.show_when). */
export function evaluateCondition(condition: PageLogicCondition, answers: FormAnswerMap): boolean {
    const raw = normaliseAnswer(answers[condition.input_id]);
    const expected = condition.value;

    switch (condition.operator) {
        case "equals":
            if (Array.isArray(raw)) return raw.map(String).includes(String(expected));
            if (typeof raw === "string" && typeof expected === "string") {
                return raw.toLowerCase() === expected.toLowerCase();
            }
            return String(raw) === String(expected) || raw === expected;
        case "not_equals":
            if (Array.isArray(raw)) return !raw.map(String).includes(String(expected));
            if (typeof raw === "string" && typeof expected === "string") {
                return raw.toLowerCase() !== expected.toLowerCase();
            }
            return String(raw) !== String(expected) && raw !== expected;
        case "includes":
            if (Array.isArray(raw)) {
                return raw.map(String).includes(String(expected));
            }
            return String(raw).includes(String(expected));
        case "includes_own_option": {
            const values = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
            return values.some((value) => isOwnOptionValue(value)) === (String(expected) !== "false");
        }
        case "greater_than":
            return compareNumbers(raw, expected, (left, right) => left > right);
        case "greater_than_or_equals":
            return compareNumbers(raw, expected, (left, right) => left >= right);
        case "less_than":
            return compareNumbers(raw, expected, (left, right) => left < right);
        case "less_than_or_equals":
            return compareNumbers(raw, expected, (left, right) => left <= right);
        default:
            return false;
    }
}

function resolveAction(action: PageLogicAction, pages: PageLogicPageRef[], currentPageId: string): PageLogicResolution {
    if (action.action === "end") return { kind: "end" };
    if (action.action === "jump") {
        const exists = pages.some((page) => page.page_id === action.target_page_id);
        return exists ? { kind: "page", page_id: action.target_page_id } : { kind: "end" };
    }

    const sorted = [...pages].sort((a, b) => a.page_index - b.page_index);
    const currentIndex = sorted.findIndex((page) => page.page_id === currentPageId);
    const nextPage = currentIndex >= 0 ? sorted[currentIndex + 1] : undefined;
    return nextPage ? { kind: "page", page_id: nextPage.page_id } : { kind: "end" };
}

export function parsePageLogic(raw: unknown): PageLogic | null {
    if (!raw || typeof raw !== "object") return null;
    const candidate = raw as PageLogic;
    if (!Array.isArray(candidate.rules) || !candidate.fallback) return null;
    return candidate;
}

export function formHasPageLogic(pages: PageLogicPageRef[]): boolean {
    return pages.some((page) => {
        const logic = parsePageLogic(page.page_logic);
        return logic != null && (logic.rules.length > 0 || logic.fallback.action !== "next");
    });
}

export function evaluatePageLogic(
    pageLogic: PageLogic | null | undefined,
    answers: FormAnswerMap,
    pages: PageLogicPageRef[],
    currentPageId: string
): PageLogicResolution {
    const logic = pageLogic ?? null;
    if (!logic) {
        return resolveAction({ action: "next" }, pages, currentPageId);
    }

    for (const rule of logic.rules) {
        if (evaluateCondition(rule.when, answers)) {
            return resolveAction(rule.then, pages, currentPageId);
        }
    }

    return resolveAction(logic.fallback, pages, currentPageId);
}

export function resolveNextPageId(
    currentPageId: string,
    pages: PageLogicPageRef[],
    answers: FormAnswerMap
): PageLogicResolution {
    const current = pages.find((page) => page.page_id === currentPageId);
    const logic = parsePageLogic(current?.page_logic);
    return evaluatePageLogic(logic, answers, pages, currentPageId);
}

export function pageIdToPageIndex(pageId: string, pages: PageLogicPageRef[]): number | null {
    const page = pages.find((candidate) => candidate.page_id === pageId);
    return page?.page_index ?? null;
}

export function getFirstPageId(pages: PageLogicPageRef[]): string | null {
    const sorted = [...pages].sort((a, b) => a.page_index - b.page_index);
    return sorted[0]?.page_id ?? null;
}
